/**
 * classifyUpgradeBlock — the single source of truth for which 403 error codes
 * are plan/trial upgrade gates, and how to shape them for the upgrade modal.
 *
 * Lives in its own module (not client.ts) so components and tests can import it
 * without pulling in client.ts's module-scope `import.meta.env` read, which
 * ts-jest's CommonJS transform can't parse (same pattern as apiError.ts).
 * client.ts re-exports it, so existing imports keep working.
 */

export type UpgradeReason = 'trial_expired' | 'plan_limit' | 'pro_feature';

export interface UpgradeBlock {
  reason: UpgradeReason;
  /** Server-provided human-readable message. */
  message: string;
  /** Pro-gated feature name, when reason is 'pro_feature'. */
  feature?: string;
  /** Plan limit and current count, when reason is 'plan_limit'. */
  limit?: number;
  current?: number;
}

/** Map a 403 error body to an UpgradeBlock, or null if it isn't a plan/trial gate. */
export function classifyUpgradeBlock(
  code: string | undefined,
  message: string,
  data: unknown,
): UpgradeBlock | null {
  const body = (data ?? {}) as { error?: unknown; limit?: number; current?: number };
  if (code === 'TRIAL_EXPIRED') {
    return { reason: 'trial_expired', message };
  }
  if (code === 'PLAN_UPGRADE_REQUIRED') {
    const feature =
      body.error && typeof body.error === 'object'
        ? (body.error as { feature?: string }).feature
        : undefined;
    return { reason: 'pro_feature', message, feature };
  }
  if (code === 'plan_limit_exceeded') {
    return { reason: 'plan_limit', message, limit: body.limit, current: body.current };
  }
  return null;
}
